import { BaseComponentDrawer } from '../../../core/abstraction/BaseComponentDrawer';
import { hold } from '../../../core/helpers/hold';
import { delay } from '../../../core/helpers/delay';
import { debounce } from '../../../core/helpers/debounce';
import {
  BONUS_RESPONSE_TOTAL_AMOUNT,
  FREE_SPINS_STATE,
  GAME_PAD_SPIN_ACTIVE,
  GAME_SCENE_STATE,
  MULTIPLIER_STATE,
  WHEEL_ROTATION_STATE,
} from '../../../stateManagment/gameState';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { FONT_LATO_NAME, GAME_SCENE, BALANCE_UPDATE_DELAY } from '../../../core/constats';
import { spinButtonHelper } from '../../../services/helperService/SpinButtonHelper';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { bonusSpinResponseHelper } from '../../../services/helperService/BonusSpinResponseHelper';
import { bonusWheelDrawer, wheelDrawer } from '../../wheel/WheelDrawer';
import { popupHelper } from '../../../services/helperService/PopupHelper';
import { bigWinService } from '../../../services/helperService/BigWinService';
import { gameService } from '../../../services/GameService';
import { gamePadHelper } from '../../../services/helperService/GamePadHelper';
import { spinResponseHelper } from '../../../services/helperService/SpinResponseHelper';
import { userService } from '../../../services/helperService/UserService';
import { historyHelperService } from '../../../services/helperService/HistoryHelperService';
import { backgroundDrawer } from '../../background/BackgroundDrawer';
import { crystalTypes } from '../../../core/gameConstants';
import { autoSpinHelper } from '../../../services/helperService/AutoSpinHelper';
import { playerService } from '../../../services/PlayerService';
import { preBonusContentDrawer } from '../../preBonusContent/PreBonusContentDrawer';
import { MESSAGE_TYPES, notificationHandler } from '../../../services/NotificationHandler';

export class BaseGamePadDrawer extends BaseComponentDrawer {
  animateSpinButton() {
    animationDrawer.spin(this.__spin_button, .5);
  }

  getSpinButton({ name, scale = 1, onClick = () => null }) {
    const container = this.createContainer();
    const spin = this.createSprite(this.resources[name]);
    spin.scale.set(scale);
    spin.anchor.set(.5);
    this.__spin_button = spin;

    this.addSubscriber(GAME_PAD_SPIN_ACTIVE.subscribe(isActive => {
      if (isActive) {
        spin.alpha = 1;
      } else {
        spin.alpha = .7;
      }
    }, true));

    this.setupButtonMode(spin);
    this.setupButtonMode(container);
    this.registerClick(container, event => event.stopPropagation());
    this.registerClick(spin, event => {
      event.stopPropagation();
      if (spinButtonHelper.active) {
        onClick();
        this.animateSpinButton();
      }
    });

    container.addChild(
      spin,
    );
    return container;
  }

  _bonusSpinClick() {
    const holding = hold();
    spinButtonHelper.disable();
    bonusSpinResponseHelper.resetError();

    const freeSpin = bonusSpinResponseHelper.getFreeSpinCount();
    if (!freeSpin) {
      return;
    }
    spinButtonHelper.disable();
    bonusSpinResponseHelper.nullify();
    bonusSpinResponseHelper.decrementFreeSpin();
    soundService.playWheel();
    bonusWheelDrawer.rotate().then(() => {
      const data = bonusSpinResponseHelper.getData();
      if (data) {
        bonusWheelDrawer.bonusWinAnimation(data);
        historyHelperService?.last?.addOdd(data.odd);
        userService.addBonusLastResult(data.result);
        backgroundDrawer.application.moveCrystal(crystalTypes[data.result], bonusWheelDrawer.containerAndWheel);
        delay(() => {
          MULTIPLIER_STATE.next(MULTIPLIER_STATE.getValue());
        }, 500);

        delay(() => {
          if (data?.totalWinAmount) {
            BONUS_RESPONSE_TOTAL_AMOUNT.next(data?.totalWinAmount);
          }
        }, 1000);
        if (typeof data.remainingSpinCount === 'number' && data.remainingSpinCount !== FREE_SPINS_STATE.getValue()) {
          FREE_SPINS_STATE.next(data.remainingSpinCount);
        }

        if (data.remainingSpinCount === 0) {
          if (bigWinService.checkBonusSpinAndStartBigWin(data)) {
            bigWinService.addCallback(() => {
              delay(() => {
                historyHelperService?.last?.update({
                  winAmount: data.totalWinAmount,
                });
                if (gameService.isRealMode) {
                  playerService.getBalance();
                } else {
                  userService.plusBalance(bonusSpinResponseHelper.getData()?.totalWinAmount);
                }
                historyHelperService.updateHistory();
                GAME_SCENE_STATE.next(GAME_SCENE.MAIN);
                spinButtonHelper.enable()
              }, 500);
            });
          } else {
            delay(() => {
              historyHelperService?.last?.update({
                winAmount: data.totalWinAmount,
              });
              popupHelper.setCongratulationsPopupState(true);
            }, 2000);
          }
        } else {
          if (bigWinService.checkBonusSpinAndStartBigWin(data)) {
            bigWinService.setHas(true);
            bonusSpinResponseHelper.pause();
          } else {
            bonusSpinResponseHelper.setDelay(
              delay(() => {
                this._bonusSpinClick();
              }, 2000),
            );
          }
        }
      }
    });
    gameService.bonusSpin().then(data => {
      holding(() => {
        bonusSpinResponseHelper.setData(data);
      }, 1000);
    }).catch(error => {
      bonusSpinResponseHelper.setError(error);
      gameService.getBonusLastState().then(data => {
        if (data) {
          this._bonusSpinClick();
        } else {
          GAME_SCENE_STATE.next(GAME_SCENE.MAIN);
          spinButtonHelper.enable()
        }
      }).catch(() => {
        window.location.reload();
      });
    });
  }

  _spinSetupInitialState(errorCase = true) {
    spinResponseHelper.stopWheelRotation();
    spinResponseHelper.enableOddCrystalActiveState();
    if (errorCase) {
      spinButtonHelper.enable();
    }
    gamePadHelper.enableAmountChangeState();
    gamePadHelper.enableSpinCountChangeState();
    spinResponseHelper.resetSpinStarted();
  }

  _autoSpinSetupInitialState() {
    spinResponseHelper.stopWheelRotation();

    if (!autoSpinHelper.autoSpinActive) {
      spinResponseHelper.setSpinStartedState();
      spinResponseHelper.enableOddCrystalActiveState();
      spinButtonHelper.enable();
    }
  }

  _spinClick() {
    const holding = hold();
    if (gameService.isRealMode && !userService.balance) {
      playerService.getBalance().then(value => {
        if (!value) {
          notificationHandler.notEnoughMoney();
          this._spinSetupInitialState();
        }
      });
      return;
    }
    if(!gameService.isRealMode && !userService.checkBalance()){
      notificationHandler.notEnoughMoney()
      this._spinSetupInitialState();
      return
    }
    spinResponseHelper.nullify();
    spinResponseHelper.resetError();
    gamePadHelper.disabledAmountChangeState();
    gamePadHelper.disabledSpinCountChangeState();
    spinResponseHelper.startWheelRotation();
    spinResponseHelper.resetResponse();
    spinResponseHelper.startSpin();
    spinResponseHelper.disableOddCrystalActiveState();
    gameService.spin().then(data => {
      soundService.playWheel();
      wheelDrawer.rotate().then(() => {
        if (!spinResponseHelper.hasError) {
          this._spinSetupInitialState();
          const data = spinResponseHelper.getData();
          if (data !== null) {
            spinButtonHelper.disable();
            // check win type
            if (data.odd && gameService.isRealMode) {
              if (holding.getTime() > BALANCE_UPDATE_DELAY) {
                playerService.getBalance();
              } else {
                playerService.holdUpdateBalance(BALANCE_UPDATE_DELAY - holding.getTime());
              }
            }
            const isBonus = this.__checkAndSetPreBonus(data);
            wheelDrawer.winAnimation(data);
            bigWinService.checkSpinAndStartBigWin(data);
            userService.updateUserData(data);
            historyHelperService.updateHistory();
            backgroundDrawer.application.moveCrystal(
              crystalTypes[data.result],
              wheelDrawer.containerAndWheel,
            ).then(() => {
              if (!isBonus) {
                spinButtonHelper.enable();
                gamePadHelper.enableAutoSpin();
              }
            });
          }
        } else {
          this._spinSetupInitialState();
        }
      })
      this.__checkAndDisableButtons(data);
      spinResponseHelper.setResponse(data);
      if (spinResponseHelper.isStop) {
        spinResponseHelper.setData(data);
        return;
      }
      holding(() => {
        spinResponseHelper.setData(data);
      }, 1000);
    }).catch(error => {
      this._spinSetupInitialState();
      spinResponseHelper.setError(error);
    });
  }

  _stopSpin() {
    spinButtonHelper.disable();
    spinResponseHelper.stopSpin();
    if (spinResponseHelper.response) {
      spinResponseHelper.setData(spinResponseHelper.response);
      spinResponseHelper.resetSpinStarted();
    }
  }

  __autoSpinClick = debounce(() => {
    const holding = hold();
    if (gameService.isRealMode && !userService.balance) {
      playerService.getBalance().then(value => {
        if (!value) {
          autoSpinHelper.stopAutoSpin();
          this._autoSpinSetupInitialState();
          gamePadHelper.enableAmountChangeState();
          spinResponseHelper.enableOddCrystalActiveState();
          gamePadHelper.enableSpinCountChangeState();
        }
      });
      return;
    }

    if(!gameService.isRealMode && !userService.checkBalance()){
      notificationHandler.notEnoughMoney()
      this._spinSetupInitialState();
      return
    }

    if (!WHEEL_ROTATION_STATE.getValue()) {
      spinResponseHelper.nullify();
    }
    spinResponseHelper.resetError();
    if (!autoSpinHelper.spinCount) {
      return;
    }
    autoSpinHelper.decrementAutoSpin();
    spinResponseHelper.startSpin();
    spinResponseHelper.resetResponse();
    spinResponseHelper.startWheelRotation();
    spinResponseHelper.disableOddCrystalActiveState();
    gamePadHelper.disabledAmountChangeState();
    gamePadHelper.disableAutoSpin();
    gamePadHelper.disabledSpinCountChangeState();
    soundService.playWheel();

    wheelDrawer.rotate().then(() => {
      this._autoSpinSetupInitialState();
      spinButtonHelper.disable();
      if (!spinResponseHelper.hasError) {
        const data = spinResponseHelper.getData();
        if (data !== null) {
          this.__checkAndSetPreBonus(data);
          wheelDrawer.winAnimation(data);
          userService.updateUserData(data);
          historyHelperService.updateHistory();
          backgroundDrawer.application.moveCrystal(
            crystalTypes[data.result],
            wheelDrawer.containerAndWheel,
          );
          if (bigWinService.checkSpinAndStartBigWin(data)) {
            bigWinService.setHas(true);
            autoSpinHelper.pause();
          }
          if (autoSpinHelper.autoSpinActive) {
            autoSpinHelper.setDelay(delay(() => {
              if (autoSpinHelper.autoSpinActive) {
                this._autoSpinClick();
              }
            }, 2000));
          } else {
            gamePadHelper.enableAmountChangeState();
            delay(() => {
              gamePadHelper.enableSpinCountChangeState();
              spinButtonHelper.enable();
              gamePadHelper.enableAutoSpin();
            }, 1500);
          }
        }
      } else {
        this._autoSpinSetupInitialState();
        spinButtonHelper.enable();
      }
    });

    gameService.spin().then(data => {
      this.__checkAndDisableButtons(data);
      spinResponseHelper.setResponse(data);
      holding(() => {
        spinResponseHelper.setData(spinResponseHelper.response);
      }, 1000);
    }).catch(error => {
      autoSpinHelper.stopAutoSpin();
      spinResponseHelper.setError(error);
      gamePadHelper.enableAmountChangeState();
      gamePadHelper.enableSpinCountChangeState();
      spinResponseHelper.enableOddCrystalActiveState();
      spinResponseHelper.stopSpin();
      spinButtonHelper.enable();
    });
  }, 3000, true)

  _autoSpinClick() {
    this.__autoSpinClick();
  }

  __checkAndSetPreBonus(data) {
    if (data.result === crystalTypes.crystal_gold) {
      popupHelper.setCongratulationsBonusPopupState(true);
      return true;
    }
    return false;
  }

  __checkAndDisableButtons(data) {
    if (data.result === crystalTypes.crystal_gold) {
      spinButtonHelper.disable();
      if (autoSpinHelper.isAutoSpin) {
        autoSpinHelper.stopAutoSpin();
      }
    }
  }

  getAutoSpinButton({
    name,
    scale = 1,
    onClick = () => null,
    countObserver,
    infinity,
  }) {
    const container = this.createContainer();
    const spin = this.createSprite(this.resources[name]);
    const infinityIcon = this.createSprite(this.resources[infinity]);
    const countText = new this.PIXI.Text(55, {
      fontFamily: FONT_LATO_NAME,
      fontSize: 22,
      fontWeight: 700,
    });

    infinityIcon.anchor.set(.5);
    infinityIcon.scale.set(.8);

    this.addSubscriber(countObserver.subscribe(value => {
      if (value !== null) {
        if (value === Infinity) {
          countText.text = '';
          infinityIcon.alpha = 1;
        } else {
          infinityIcon.alpha = 0;
          countText.text = value;
        }
      }
    }, true));
    countText.anchor.set(.5);
    spin.scale.set(scale);
    spin.anchor.set(.5);
    spin.interactive = true;
    spin.buttonMode = true;

    spin.on('pointerdown', (event) => {
      event.stopPropagation();
      onClick();
    });
    container.addChild(
      spin,
      countText,
      infinityIcon,
    );
    return container;
  }

  _createGamePadCircle({
    icon,
    activeBorderIcon,
    notActiveBorderIcon,
    w,
    h,
    scale = .9,
    observer,
    checkButtonActive = () => true,
    title = '',
    onToggle = () => null,
    titleObserver,
  }) {

    const titleTexture = new this.PIXI.Text(title, {
      fill: 0xFFFFFF,
      fontFamily: FONT_LATO_NAME,
      fontSize: 25,
      fontWeight: 900,
    });


    const container = this.createContainer();
    const controllerContainer = this.createContainer();
    const image = this.createSprite(this.resources[icon]);
    const activeBorder = this.createSprite(this.resources[activeBorderIcon]);
    const notActiveBorder = this.createSprite(this.resources[notActiveBorderIcon]);
    this.setSizes(notActiveBorder, { w, h });
    this.setSizes(activeBorder, { w, h });
    image.anchor.set(.5);
    titleTexture.anchor.set(.5);
    activeBorder.anchor.set(.5);
    notActiveBorder.anchor.set(.5);
    image.scale.set(scale);

    const rect = new this.PIXI.Graphics()
      .drawRect(0, 0, w, h)
      .endFill();

    const circle = new this.PIXI.Graphics()
      .beginFill(0x000000)
      .drawCircle(0, 0, w / 2)
      .endFill();

    circle.alpha = .7;
    circle.interactive = true;
    circle.buttonMode = true;

    circle.on('pointerdown', (event) => {
      soundService.playSingleClick();
      event.stopPropagation();
      if (checkButtonActive()) {
        const autoSpinButtonActive = observer.getValue();
        onToggle(autoSpinButtonActive);
        observer.next(!autoSpinButtonActive);
      }
    });

    rect.addChild(
      activeBorder,
      notActiveBorder,
    );

    container.addChild(
      controllerContainer,
      circle,
      rect,
      image,
      titleTexture,
    );
    if (titleTexture) {
      container.addChild(titleTexture);
    }

    if (titleObserver) {
      this.addSubscriber(titleObserver.subscribe(isActive => {
        if (observer.getValue()) {
          if (isActive) {
            titleTexture.alpha = 1;
          } else {
            titleTexture.alpha = .5;
          }
        }
      }));
    }

    this.addSubscriber(observer.subscribe(value => {
      activeBorder.visible = value;
      notActiveBorder.visible = !value;
      if (title) {

        titleTexture.alpha = +value;
        image.visible = !value;
        if (titleObserver && value) {
          titleTexture.alpha = titleObserver.getValue() ? 1 : .5;
        }
      }
    }, true));

    return {
      container,
      controllerContainer,
      titleTexture,
    };
  }

  getHelperButton({
    icon,
    observer,
    activeBorderIcon,
    notActiveBorderIcon,
    sizes,
    checkButtonActive,
    title = null,
    onToggle,
    titleObserver,
  }) {
    const { container, controllerContainer } = this._createGamePadCircle({
      ...sizes,
      icon,
      activeBorderIcon,
      notActiveBorderIcon,
      defaultState: observer.getValue(),
      observer,
      checkButtonActive,
      title,
      onToggle,
      titleObserver,
    });
    return { container, controllerContainer };
  }

  getSmallButton({ src, scale = 1 }) {
    const button = this.createSprite(this.resources[src]);
    button.scale.set(scale);
    button.anchor.set(.5);
    return button;
  }

  setupToggle({ root, content, observer }) {
    if (observer.getValue()) {
      root.addChild(content);
    }
    this.addSubscriber(observer.subscribe(toggle => {
      if (toggle) {
        root.addChild(content);
      } else {
        root.children = [];
      }
    }));
  }

  setupTextUpdate({
    textTexture,
    observer,
    indexObserver,
    renderOption = val => val,
  }) {
    textTexture.text = observer.getValue()[indexObserver.getValue()];
    this.addSubscriber(observer.subscribe(values => {
      textTexture.text = renderOption(values[indexObserver.getValue()], textTexture);
    }));

    this.addSubscriber(indexObserver.subscribe(index => {
      textTexture.text = renderOption(observer.getValue()[index], textTexture);
    }));
  }

  setupButtonActiveInActiveCase({ prev, next, observer, indexObserver, opacity: [min = .5, max = 1] = [] }) {
    const initialIndex = indexObserver.getValue();
    const { length } = observer.getValue();

    initialIndex === 0 ? prev.alpha = min : prev.alpha = max;
    initialIndex === length - 1 ? next.alpha = min : next.alpha = max;

    this.addSubscriber(indexObserver.subscribe(index => {
      index === 0 ? prev.alpha = min : prev.alpha = max;
      index === observer.getValue().length - 1 ? next.alpha = min : next.alpha = max;
    }));

    this.addSubscriber(observer.subscribe(values => {
      const index = indexObserver.getValue();
      index === 0 ? prev.alpha = .5 : prev.alpha = 1;
      index === values.length - 1 ? next.alpha = .5 : next.alpha = 1;
    }));
  }

  getNextButtonClick({ observer, indexObserver }) {
    let index = indexObserver.getValue();
    if (observer.getValue().length - 1 > index) {
      indexObserver.next(++index);
    }
  }

  getPrevButtonClick({ indexObserver }) {
    let index = indexObserver.getValue();
    if (0 < index) {
      indexObserver.next(--index);
    }
  }

  getGamePadController({
    wallpaperIcon,
    prevIcon,
    nextIcon,
    title,
    drawCircle = false,
    events: { handlePrev, handleNext } = { handlePrev: () => null, handleNext: () => null },
    sizes: {
      width,
      prevPosition = { x: 44, y: 78 },
      nextPosition = { x: 209, y: 78 },
      titlePosition = { x: width / 2, y: 20 },
      valuePosition = { x: width / 2, y: 78 },
    } = {},
  }) {
    const content = this.createContainer();
    const valueTextContainer = this.createContainer('valueTextContainer');
    const wallpaper = this.createSprite(this.resources[wallpaperIcon]);

    let prev = this.getSmallButton({ src: prevIcon, scale: 1 });
    let next = this.getSmallButton({ src: nextIcon, scale: 1 });
    const prev_circle = this.createGraphics().beginFill(0x636363).drawCircle(
      0, 0, prev.width / 2,
    );

    const next_circle = this.createGraphics().beginFill(0x636363).drawCircle(
      0, 0, next.width / 2,
    );

    prev_circle.interactive = true;
    prev_circle.buttonMode = true;
    next_circle.interactive = true;
    next_circle.buttonMode = true;

    const contentText = new this.PIXI.Text(title, {
      fill: 0xFFFFFF,
      fontSize: 26,
      align: 'center',
      fontWeight: 600,
    });
    contentText.anchor.set(.5);
    const valueText = new this.PIXI.Text('', {
      fill: 0xFFFFFF,
      fontSize: 26,
      align: 'center',
    });
    valueText.name = 'VALUE_TEXT';
    valueText.anchor.set(.5);
    wallpaper.scale.set(wallpaper.width / width);

    this.setPosition(prev_circle, prevPosition);
    this.setPosition(prev, prevPosition);
    this.setPosition(next_circle, nextPosition);
    this.setPosition(next, nextPosition);
    this.setPosition(contentText, titlePosition);
    this.setPosition(valueTextContainer, valuePosition);

    if (!drawCircle) {
      prev_circle.alpha = 0;
      next_circle.alpha = 0;
    }

    prev_circle.on('pointerdown', () => handlePrev({ target: prev }));
    next_circle.on('pointerdown', () => handleNext({ target: next }));
    valueTextContainer.addChild(valueText);
    content.addChild(
      wallpaper,
      prev_circle,
      prev,
      next_circle,
      next,
      contentText,
      valueTextContainer,
    );
    return {
      content,
      valueText,
      prev: drawCircle ? prev_circle : prev,
      next: drawCircle ? next_circle : next,
    };
  }

  handleSpinButtonClick() {
    return debounce(() => {
      gamePadHelper.disabledAmountChangeState();
      gamePadHelper.disabledSpinCountChangeState();
      spinResponseHelper.disableOddCrystalActiveState();

      if (spinResponseHelper.spinStarted) {
        this._stopSpin();
      } else {
        this._spinClick();
      }
      //      if (autoSpinHelper.checkAutoSpin(gamePadLocaleState.autoSpinButtonActive.getValue())) {
      //         autoSpinHelper.spinCount = autoSpinHelper.spinCountByControl;
      //         autoSpinHelper.startAutoSpin();
      //         gamePadHelper.disabledAmountChangeState();
      //         gamePadHelper.disabledSpinCountChangeState();
      //         this._autoSpinClick();
      //       } else {

      this.resetOpenedState();
    }, 300, true);
  }

  handleAutoSpinClick() {
    if (gamePadHelper.autoSpinButtonState) {
      autoSpinHelper.spinCount = autoSpinHelper.spinCountByControl;
      autoSpinHelper.startAutoSpin();
      gamePadHelper.disabledAmountChangeState();
      gamePadHelper.disableAutoSpin();
      gamePadHelper.disabledSpinCountChangeState();
      this._autoSpinClick();
    }
  }

  handleAutoSpinButtonClick() {
    return () => {
      autoSpinHelper.stopAutoSpin();
      spinButtonHelper.disable();
      gamePadHelper.disableAutoSpin()
      if (!WHEEL_ROTATION_STATE.getValue()) {
        gamePadHelper.enableAmountChangeState();
        gamePadHelper.enableSpinCountChangeState();
        spinButtonHelper.enable();
        spinResponseHelper.enableOddCrystalActiveState();
        spinResponseHelper.stopSpin();
      }
    };
  }

  handlePreBonusSpinButtonClick() {
    return debounce(() => preBonusContentDrawer.spin(), 300, true);
  }

  handleBonusClick() {
    this._bonusSpinClick();
  }
}
